// Validador pós-cálculo: depois da rota ser computada (HERE/Google/
// Híbrido), verifica se algum incidente crítico de qualquer provider
// (HERE Traffic + TomTom Traffic) cai sobre o trajecto.
//
// Filosofia: NÃO faz re-routing automático. Limita-se a detectar e
// avisar — o operador decide se quer recalcular a rota com avoid
// areas ou prosseguir mesmo assim.

import { fetchIncidentsForViewport } from './hereApi';
import { fetchIncidentsTomTomBbox } from './tomtomApi';

function haversineM(a, b) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Distância mínima de um ponto a uma polilinha — testa contra cada
// vértice (suficiente para corredor de 500m + sub-amostragem dos
// pontos da rota; não vale a pena projecção exacta no segmento).
function minDistanceToLine(point, line) {
  let min = Infinity;
  for (const p of line) {
    const d = haversineM(point, p);
    if (d < min) min = d;
  }
  return min;
}

// Constrói uma bbox que envolve a rota com uma margem em metros.
// Usado para limitar a área de pesquisa de incidentes — não vale a
// pena ir buscar incidentes em Madrid quando a rota é Lisboa-Porto.
function routeBoundingBox(routeCoords, marginM = 2000) {
  if (!Array.isArray(routeCoords) || routeCoords.length === 0) return null;
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  for (const [lng, lat] of routeCoords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  // Margem ~ 2km — graus aproximados (1° lat ≈ 111km, 1° lng varia)
  const dLat = marginM / 111320;
  const dLng = marginM / (111320 * Math.cos((minLat + maxLat) / 2 * Math.PI / 180));
  return {
    south: minLat - dLat,
    north: maxLat + dLat,
    west: minLng - dLng,
    east: maxLng + dLng,
  };
}

// Subamostra a rota para baixar o custo do test ponto-vs-linha.
// 200 pontos é mais que suficiente para detectar intersecções a 500m.
function subsampleRoute(coords, maxPoints = 200) {
  if (!Array.isArray(coords) || coords.length <= maxPoints) return coords || [];
  const step = Math.ceil(coords.length / maxPoints);
  const out = [];
  for (let i = 0; i < coords.length; i += step) {
    out.push({ lat: coords[i][1], lng: coords[i][0] });
  }
  return out;
}

/**
 * Para cada incidente, testa se cai dentro de `corridorM` do trajecto.
 * Retorna apenas os críticos (filtra ruído de baixa severidade).
 */
function filterCriticalOnRoute(incidents, routeSamples, corridorM) {
  const result = [];
  for (const inc of (incidents || [])) {
    if (!Array.isArray(inc.coordinates)) continue;
    const point = inc.coordinates;
    const dist = minDistanceToLine(point, routeSamples);
    if (dist > corridorM) continue;
    // Critério "crítico" — operador só quer ver o que MESMO afecta:
    //   • via fechada (sempre relevante)
    //   • criticality 'critical' ou 'major'
    //   • acidente / obras / cortes (independente de criticality)
    const isCritical = inc.roadClosed === true
      || inc.criticality === 'critical'
      || inc.criticality === 'major'
      || inc.type === 0   // acidente
      || inc.type === 3;  // estrada cortada
    // Obras / construction só interessam se forem severas — caso
    // contrário enchem o painel de aviso com obras menores.
    const isConstruction = inc.type === 2;
    const isSevereConstruction = isConstruction
      && (inc.criticality === 'critical' || inc.criticality === 'major' || inc.roadClosed);
    if (!isCritical && !isSevereConstruction) continue;
    result.push({
      ...inc,
      _routeDistanceM: Math.round(dist),
    });
  }
  // Ordenar por distância à rota (mais próximos primeiro)
  result.sort((a, b) => a._routeDistanceM - b._routeDistanceM);
  return result;
}

/**
 * Valida uma rota contra incidentes em tempo real.
 *
 * @param {Array<[number,number]>} routeCoords  geometria como [[lng,lat]...]
 * @param {Object} options
 * @param {number} options.corridorM  largura do corredor em metros (default 500)
 * @returns {Promise<{incidents: Array, fetched: {here:number,tomtom:number}}>}
 *   Lista de incidentes críticos sobre a rota + estatísticas das
 *   chamadas para o operador saber que provider trouxe o quê.
 */
export async function validateRouteIncidents(routeCoords, options = {}) {
  const corridorM = options.corridorM || 500;
  if (!Array.isArray(routeCoords) || routeCoords.length < 2) {
    return { incidents: [], fetched: { here: 0, tomtom: 0 } };
  }

  const bbox = routeBoundingBox(routeCoords);
  if (!bbox) return { incidents: [], fetched: { here: 0, tomtom: 0 } };

  // Fetch ambas as fontes em paralelo. Falhas individuais são
  // toleradas — se HERE cair, ainda apresentamos os do TomTom e
  // vice-versa.
  const [hereList, tomtomList] = await Promise.all([
    fetchIncidentsForViewport(bbox).catch(() => []),
    fetchIncidentsTomTomBbox(bbox).catch(() => []),
  ]);

  const samples = subsampleRoute(routeCoords);
  const taggedHere = (hereList || []).map(x => ({ ...x, source: 'here' }));
  const all = [...taggedHere, ...(tomtomList || [])];

  // Deduplicar HERE+TomTom por proximidade (~55m) — o mesmo incidente
  // reportado por ambos não deve aparecer duas vezes no banner.
  const dedupRadius = 0.0005;
  const merged = [];
  for (const inc of all) {
    const dupe = merged.findIndex(x =>
      Math.abs(x.coordinates[0] - inc.coordinates[0]) < dedupRadius &&
      Math.abs(x.coordinates[1] - inc.coordinates[1]) < dedupRadius
    );
    if (dupe === -1) merged.push(inc);
    else merged[dupe] = { ...merged[dupe], alsoReportedBy: inc.source };
  }

  const critical = filterCriticalOnRoute(merged, samples, corridorM);

  return {
    incidents: critical,
    fetched: {
      here: (hereList || []).length,
      tomtom: (tomtomList || []).length,
    },
  };
}
